import { SelectedReferenceTypeInfo } from '@ardoq/settings-bar';
import {
  AxisSortOrder,
  ComponentMatrix2Axis,
  ComponentMatrix2StoredSettableSettings,
  ComponentMatrix2ViewSettings,
  SettableComponentMatrix2ViewSettings,
} from './types';

/** One axis as the persisted settings hold it: a name per way of grouping. */
type StoredAxis = {
  selectedFieldNames: string[];
  referenceType: SelectedReferenceTypeInfo | null;
  useParentAs: boolean;
  sortOrder: AxisSortOrder | undefined;
  displayTextInView: string | null | undefined;
  additionalHeaderFieldNames: string[] | undefined;
};

const storedXAxis = (
  settings: ComponentMatrix2StoredSettableSettings
): StoredAxis => ({
  selectedFieldNames: settings.selectedFieldNamesX,
  referenceType: settings.referenceTypeX,
  useParentAs: settings.useParentAsX,
  sortOrder: settings.sortOrderX,
  displayTextInView: settings.displayTextInViewX,
  additionalHeaderFieldNames: settings.additionalHeaderFieldNamesX,
});

const storedYAxis = (
  settings: ComponentMatrix2StoredSettableSettings
): StoredAxis => ({
  selectedFieldNames: settings.selectedFieldNamesY,
  referenceType: settings.referenceTypeY,
  useParentAs: settings.useParentAsY,
  sortOrder: settings.sortOrderY,
  displayTextInView: settings.displayTextInViewY,
  additionalHeaderFieldNames: settings.additionalHeaderFieldNamesY,
});

const toAxis = ({
  selectedFieldNames,
  referenceType,
  useParentAs,
  sortOrder,
  displayTextInView,
  additionalHeaderFieldNames,
}: StoredAxis): ComponentMatrix2Axis => {
  const display = {
    sortOrder: sortOrder ?? AxisSortOrder.DEFAULT,
    displayText: displayTextInView ?? null,
    additionalHeaderFieldNames,
  };

  if (useParentAs) {
    return { kind: 'parent', ...display };
  }
  if (referenceType) {
    return { kind: 'reference', reference: referenceType, ...display };
  }
  if (selectedFieldNames.length > 0) {
    return { kind: 'field', fieldName: selectedFieldNames[0], ...display };
  }
  return { kind: 'none' };
};

const toStoredAxis = (axis: ComponentMatrix2Axis): StoredAxis => {
  if (axis.kind === 'none') {
    return {
      selectedFieldNames: [],
      referenceType: null,
      useParentAs: false,
      sortOrder: AxisSortOrder.DEFAULT,
      displayTextInView: null,
      additionalHeaderFieldNames: [],
    };
  }

  const display = {
    sortOrder: axis.sortOrder,
    displayTextInView: axis.displayText,
    additionalHeaderFieldNames: axis.additionalHeaderFieldNames,
  };

  switch (axis.kind) {
    case 'field':
      return {
        selectedFieldNames: [axis.fieldName],
        referenceType: null,
        useParentAs: false,
        ...display,
      };
    case 'reference':
      return {
        selectedFieldNames: [],
        referenceType: axis.reference,
        useParentAs: false,
        ...display,
      };
    case 'parent':
      return {
        selectedFieldNames: [],
        referenceType: null,
        useParentAs: true,
        ...display,
      };
  }
};

const withXNames = (
  axis: StoredAxis
): Partial<ComponentMatrix2ViewSettings> => ({
  selectedFieldNamesX: axis.selectedFieldNames,
  referenceTypeX: axis.referenceType,
  useParentAsX: axis.useParentAs,
  ...(axis.sortOrder !== undefined && { sortOrderX: axis.sortOrder }),
  ...(axis.displayTextInView !== undefined && {
    displayTextInViewX: axis.displayTextInView,
  }),
  ...(axis.additionalHeaderFieldNames !== undefined && {
    additionalHeaderFieldNamesX: axis.additionalHeaderFieldNames,
  }),
});

const withYNames = (
  axis: StoredAxis
): Partial<ComponentMatrix2ViewSettings> => ({
  selectedFieldNamesY: axis.selectedFieldNames,
  referenceTypeY: axis.referenceType,
  useParentAsY: axis.useParentAs,
  ...(axis.sortOrder !== undefined && { sortOrderY: axis.sortOrder }),
  ...(axis.displayTextInView !== undefined && {
    displayTextInViewY: axis.displayTextInView,
  }),
  ...(axis.additionalHeaderFieldNames !== undefined && {
    additionalHeaderFieldNamesY: axis.additionalHeaderFieldNames,
  }),
});

/** The persisted settings as one composed axis per direction. */
export const toAiComponentMatrix2ViewSettings = (
  settings: ComponentMatrix2StoredSettableSettings
): SettableComponentMatrix2ViewSettings => ({
  componentTypes: settings.selectedComponentTypes,
  xAxis: toAxis(storedXAxis(settings)),
  yAxis: toAxis(storedYAxis(settings)),
  showEmptyValues: settings.showEmptyValues,
  colorHeadersByComponent: settings.colorRowColumnHeaderByComponent,
  isLegendActive: settings.isLegendActive,
});

/** A composed patch as the names the view reads, leaving out what it omits. */
export const toStoredComponentMatrix2ViewSettings = (
  settings: Partial<SettableComponentMatrix2ViewSettings>
): Partial<ComponentMatrix2ViewSettings> => ({
  ...(settings.componentTypes !== undefined && {
    selectedComponentTypes: settings.componentTypes,
  }),
  ...(settings.xAxis !== undefined && withXNames(toStoredAxis(settings.xAxis))),
  ...(settings.yAxis !== undefined && withYNames(toStoredAxis(settings.yAxis))),
  ...(settings.showEmptyValues !== undefined && {
    showEmptyValues: settings.showEmptyValues,
  }),
  ...(settings.colorHeadersByComponent !== undefined && {
    colorRowColumnHeaderByComponent: settings.colorHeadersByComponent,
  }),
  ...(settings.isLegendActive !== undefined && {
    isLegendActive: settings.isLegendActive,
  }),
});
